import { VOMedia } from "../model/DataStructure.js";

export interface MediaItemOptions {
    element?: HTMLDivElement;
    autoplay?: boolean;
    loop?: boolean;
    controls?: boolean;
}

export class MediaItem {
    public element: HTMLDivElement;

    private _background: HTMLDivElement;
    private _image: HTMLDivElement;
    private _videoContainer: HTMLDivElement;
    private _video: HTMLVideoElement;
    private _author: HTMLDivElement;
    private _current: VOMedia = null;
    private _bar: HTMLDivElement;

    constructor(options: MediaItemOptions) {

        options = e5.ObjectUtil.extend(
            {
                autoplay: false,
                loop: false,
                controls: true
            } as MediaItemOptions, options);

        this.element = options.element || document.createElement("div");
        this.element.classList.add("media-item");
        this.element.innerHTML = `
        <div class="media-item__background"></div>
        <div class="media-item__image"></div>
        <div class="media-item__video-container">
            <video class="media-item__video" disablePictureInPicture controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"></video>
            <div class="media-item__controls">
                <div class="media-item__controls-buttons"></div>
                <div class="media-item__controls-track">
                    <div class="media-item__controls-bar"></div>
                    <div class="media-item__controls-handle"></div>
                </div>
            </div>
            <div class="media-item__toggle ${options.autoplay ? "" : "media-item__toggle--paused"}">
                <div class="media-item__toggle-icon media-item__toggle-icon--playing fal fa-play-circle"></div>
                <div class="media-item__toggle-icon media-item__toggle-icon--paused fal fa-play-circle"></div>
            </div>
        </div>
        <div class="media-item__author"></div>
        `;

        this._background = this.element.querySelector(".media-item__background");
        this._image = this.element.querySelector(".media-item__image");
        this._videoContainer = this.element.querySelector(".media-item__video-container");
        this._video = this.element.querySelector(".media-item__video");
        this._video.autoplay = options.autoplay;
        this._video.loop = options.loop;
        // this._video.controls = options.controls;
        this._author = this.element.querySelector(".media-item__author");
        this._bar = this.element.querySelector(".media-item__controls-bar");

        if (options.controls) {
            this.setControls();
        }

        if(!options.loop){
            this._video.addEventListener("ended", () => {
                this.stop();
            });
        }
    }

    private setControls(): void {

        var buttons = this.element.querySelector(".media-item__controls-buttons");
        var time = document.createElement("div");
        time.className = "media-item__control-time media-item__control-time--hidden";
        time.innerHTML = `
        <div class="media-item__time-current"></div>
        <div class="media-item__time-duration"></div>
        `;
        buttons.appendChild(time);
        var timeCurrent = time.querySelector(".media-item__time-current");
        var timeDuration = time.querySelector(".media-item__time-duration");

        var setTime = (ratio: number) => {

            var visible = this._video.duration > 0;
            time.classList.toggle("media-item__control-time--hidden", !visible);
            if (!visible) {
                return;
            }

            var sec = Math.floor(this._video.duration * ratio);
            timeCurrent.textContent = formatTime(sec);
            timeDuration.textContent = formatTime(this._video.duration);
        }



        this._video.addEventListener("loadedmetadata", () => {
            setTime(this._video.currentTime / this._video.duration);
        });

        var formatTime = (sec: number): string => {
            // if (isNaN(sec) || sec <= 0) {
            //     return "--:--";
            // }

            sec = Math.floor(sec);
            var hours = Math.floor(sec / 3600);
            var min = Math.floor(sec / 60);
            var seconds = sec % 60;
            var res = "";
            if (hours > 0) {
                res += hours + ":";
            }
            res += e5.Text.fillHead(min.toString(), 2, "0") + ":";
            res += e5.Text.fillHead(seconds.toString(), 2, "0");
            return res;
        }

        /**
         * Progress bar
         */
        var track = this.element.querySelector<HTMLDivElement>(".media-item__controls-track");
        var move = new e5.Actor(track, {
            stopPropagation: true
        });
        var getRatio = () => {
            var local = e5.Transform.toLocal(track, move.pointer.x, move.pointer.y);
            var ratio = e5.Calc.clamp(local.x / track.offsetWidth, 0, 1);
            return ratio;
        }
        move.onMove.add(() => {
            var ratio = getRatio();
            this._bar.style.width = (ratio * 100).toFixed(3) + "%";
            setTime(ratio);
        });
        move.onClear.add(() => {
            var ratio = getRatio();
            this._video.currentTime = this._video.duration * ratio;
        });
        this._video.addEventListener("timeupdate", () => {

            if (move.pointers.length > 0) {
                return;
            }

            if (!this._video.duration) {
                this._bar.style.width = "0";
                setTime(0);
                return;
            }
            var ratio = e5.Calc.clamp(this._video.currentTime / this._video.duration, 0, 1);
            this._bar.style.width = (ratio * 100).toFixed(3) + "%";
            setTime(ratio);
        });


        /**
         * Toggle
         */
        var toggle = this.element.querySelector<HTMLDivElement>(".media-item__toggle");
        var changeButtonState = () => {
            toggle.classList.toggle("media-item__toggle--paused", this._video.paused || this._video.ended);
        };
        this._video.addEventListener("abort", changeButtonState, false);
        this._video.addEventListener("error", changeButtonState, false);
        this._video.addEventListener("canplay", changeButtonState, false);
        this._video.addEventListener('play', changeButtonState, false);
        this._video.addEventListener('pause', changeButtonState, false);

        var click = new e5.Click(this._video);
        click.onClick.add(() => {

            if ((Date.now() - this._loadTimestamp) < 100) {
                return;
            }

            if (this._video.paused || this._video.ended) {
                this._video.play();
            }
            else {
                this._video.pause();
            }
        });
    }

    private _loadTimestamp = 0;
    public load(media: VOMedia): void {
        if (!media) {
            //TODO: clear, show error...
            return;
        }

        this._loadTimestamp = Date.now();

        this._current = media;

        this._image.classList.toggle("hidden", media.type !== "image");
        this._videoContainer.classList.toggle("hidden", media.type !== "video");

        if (media.type !== "video") {
            this.clearVideo();
        }

        if (media.type === "video") {
            this._background.style.backgroundImage = `url(${media.sizes.large})`;
            this._video.src = media.url;
            this._video.poster = media.sizes.large;
            this._video.load();
        } else {
            this._background.style.backgroundImage = `url(${media.url})`;
            this._image.style.backgroundImage = `url(${media.url})`;
        }

        this._author.classList.toggle("hidden", !Boolean(media.author));
        this._author.innerHTML = media.author;
        this._author.setAttribute("data-position", media.type == "video" ? "top-left" : "bottom-left");
    }

    private clearVideo(): void {
        this._video.pause();
        this._video.removeAttribute('src');
        this._video.load();
    }

    public clear(): void {
        this.clearVideo();
    }

    public stop(): void {
        if (!this._current
            || this._current.type !== "video") {
            return;
        }
        this.clearVideo();
        this._video.src = this._current.url;
        this._video.load();
    }

    private pause(): void {
        if (!this._current
            || this._current.type !== "video") {
            return;
        }
        this._video.pause();
    }

    private play(): void {
        if (!this._current
            || this._current.type !== "video") {
            return;
        }
        this._video.play();
    }
}